/**
 * @file src/utils/validation.js
 * @description Shared pure validation functions used across the application.
 *
 * Rules:
 *  - Pure functions only — no React, no axios, no side effects
 *  - Every function returns either null (valid) or a string (error message)
 *  - All regex patterns imported from config/constants — never duplicated
 *  - Fully unit-testable with no setup required
 *
 * Usage:
 *  const error = validateVatNumber("310122393500003");
 *  if (error) setErrors({ vatNumber: error });
 */

import {
  VAT_NUMBER_REGEX,
  BUILDING_NUMBER_REGEX,
  POSTAL_CODE_REGEX,
  SAUDI_PHONE_REGEX,
  EMAIL_REGEX,
  SELLER_SCHEME_IDS,
} from "config/constants";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Check if a value is empty (null, undefined, or blank string).
 * @param {*} value
 * @returns {boolean}
 */
export const isEmpty = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

/**
 * Validate a required field.
 * @param {*}      value
 * @param {string} [fieldLabel="This field"] - Used in the error message
 * @returns {string|null}
 */
export const validateRequired = (value, fieldLabel = "This field") =>
  isEmpty(value) ? `${fieldLabel} is required` : null;

// ---------------------------------------------------------------------------
// ZATCA — Company / Seller fields
// ---------------------------------------------------------------------------

/**
 * Validate a Saudi VAT number.
 * Rules (BR-KSA-40):
 *  - Exactly 15 digits
 *  - First digit must be 3
 *  - Last digit must be 3
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateVatNumber = (value) => {
  if (isEmpty(value)) return "VAT number is required";
  if (!VAT_NUMBER_REGEX.test(value)) {
    return "VAT number must be 15 digits, starting and ending with 3";
  }
  return null;
};

/**
 * Validate a building number.
 * Rule (BR-KSA-37): exactly 4 digits.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateBuildingNumber = (value) => {
  if (isEmpty(value)) return "Building number is required";
  if (!BUILDING_NUMBER_REGEX.test(value)) {
    return "Building number must be exactly 4 digits";
  }
  return null;
};

/**
 * Validate a Saudi postal code.
 * Rule (BR-KSA-66): exactly 5 digits.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validatePostalCode = (value) => {
  if (isEmpty(value)) return "Postal code is required";
  if (!POSTAL_CODE_REGEX.test(value)) {
    return "Postal code must be exactly 5 digits";
  }
  return null;
};

/**
 * Validate a seller scheme ID.
 * Rule (BR-KSA-08): must be one of the approved ZATCA scheme IDs.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateSchemeId = (value) => {
  if (isEmpty(value)) return "Scheme ID is required";
  const validIds = SELLER_SCHEME_IDS.map((s) => s.value);
  if (!validIds.includes(value)) {
    return `Scheme ID must be one of: ${validIds.join(", ")}`;
  }
  return null;
};

/**
 * Validate a registration number ID (CRN or equivalent).
 * Must be non-empty and alphanumeric only (BR-KSA-08).
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateRegistrationId = (value) => {
  if (isEmpty(value)) return "Registration number is required";
  if (!/^[a-zA-Z0-9]+$/.test(value)) {
    return "Registration number must contain only letters and numbers";
  }
  return null;
};

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

/**
 * Validate a Saudi phone number in E.164 format.
 * Optional field — returns null if empty.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateSaudiPhone = (value) => {
  if (isEmpty(value)) return null; // Optional
  if (!SAUDI_PHONE_REGEX.test(value)) {
    return "Phone must be in format +9665XXXXXXXX";
  }
  return null;
};

/**
 * Validate an email address.
 * Optional field — returns null if empty.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateEmail = (value) => {
  if (isEmpty(value)) return null; // Optional
  if (!EMAIL_REGEX.test(value)) {
    return "Please enter a valid email address";
  }
  return null;
};

// ---------------------------------------------------------------------------
// Invoice fields
// ---------------------------------------------------------------------------

/**
 * Validate an invoice number / sequential ID.
 * Must be non-empty.
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateInvoiceNumber = (value) =>
  isEmpty(value) ? "Invoice number is required" : null;

/**
 * Validate a monetary amount.
 * Must be a positive number with up to 2 decimal places (BR-DEC rules).
 *
 * @param {number|string} value
 * @returns {string|null}
 */
export const validateAmount = (value) => {
  if (isEmpty(value)) return "Amount is required";
  const num = Number(value);
  if (isNaN(num) || num < 0) return "Amount must be a positive number";
  if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
    return "Amount must have at most 2 decimal places";
  }
  return null;
};

/**
 * Validate a VAT rate percentage.
 * Must be between 0.00 and 100.00 with max 2 decimals (BR-KSA-DEC-02).
 *
 * @param {number|string} value
 * @returns {string|null}
 */
export const validateVatRate = (value) => {
  if (isEmpty(value)) return "VAT rate is required";
  const num = Number(value);
  if (isNaN(num) || num < 0 || num > 100) {
    return "VAT rate must be between 0 and 100";
  }
  if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
    return "VAT rate must have at most 2 decimal places";
  }
  return null;
};

// ---------------------------------------------------------------------------
// Step-level validators
// Used by the company onboarding wizard and any future multi-step forms.
// Returns a flat errors object: { fieldName: errorString }
// ---------------------------------------------------------------------------

/**
 * Validate company onboarding Step 1 — Company Info.
 * @param {{ legalName, vatNumber, registrationNumber: { id, schemeId } }} formData
 * @returns {Record<string, string>} Flat errors object
 */
export const validateCompanyStep1 = (formData) => {
  const errors = {};

  const legalNameErr = validateRequired(
    formData.legalName,
    "Company legal name",
  );
  if (legalNameErr) errors.legalName = legalNameErr;

  const vatErr = validateVatNumber(formData.vatNumber);
  if (vatErr) errors.vatNumber = vatErr;

  const regIdErr = validateRegistrationId(formData.registrationNumber?.id);
  if (regIdErr) errors["registrationNumber.id"] = regIdErr;

  const schemeErr = validateSchemeId(formData.registrationNumber?.schemeId);
  if (schemeErr) errors["registrationNumber.schemeId"] = schemeErr;

  return errors;
};

/**
 * Validate company onboarding Step 2 — Address.
 * @param {{ buildingNumber, street, district, city, countrySubentity, postalCode }} formData
 * @returns {Record<string, string>} Flat errors object
 */
export const validateCompanyStep2 = (formData) => {
  const errors = {};

  const buildingErr = validateBuildingNumber(formData.buildingNumber);
  if (buildingErr) errors.buildingNumber = buildingErr;

  const streetErr = validateRequired(formData.street, "Street name");
  if (streetErr) errors.street = streetErr;

  const districtErr = validateRequired(formData.district, "District");
  if (districtErr) errors.district = districtErr;

  const cityErr = validateRequired(formData.city, "City");
  if (cityErr) errors.city = cityErr;

  const subentityErr = validateRequired(
    formData.countrySubentity,
    "Province / region",
  );
  if (subentityErr) errors.countrySubentity = subentityErr;

  const postalErr = validatePostalCode(formData.postalCode);
  if (postalErr) errors.postalCode = postalErr;

  return errors;
};

/**
 * Validate company onboarding Step 3 — Contact (all optional).
 * @param {{ phone, email }} formData
 * @returns {Record<string, string>} Flat errors object
 */
export const validateCompanyStep3 = (formData) => {
  const errors = {};

  const phoneErr = validateSaudiPhone(formData.phone);
  if (phoneErr) errors.phone = phoneErr;

  const emailErr = validateEmail(formData.email);
  if (emailErr) errors.email = emailErr;

  return errors;
};

/**
 * Run the correct step validator by step number.
 * Single entry point for the wizard — keeps the wizard hook clean.
 *
 * @param {number} step  - 1 | 2 | 3
 * @param {object} formData
 * @returns {Record<string, string>} Flat errors object (empty = valid)
 */
export const validateOnboardingStep = (step, formData) => {
  switch (step) {
    case 1:
      return validateCompanyStep1(formData);
    case 2:
      return validateCompanyStep2(formData);
    case 3:
      return validateCompanyStep3(formData);
    default:
      return {};
  }
};
